import { useEffect, useMemo, useRef, useState } from 'react';
import { documentReady, titleFromFileName } from '@/onboarding/pickContent';
// The PURE splitter — the same one the server runs. Reaching for it through `textAsset.ts`
// would drag Cognito, and React Native, into the wizard.
import { previewParts } from '@/webapp/documentSplit';
import { useDraft, type DraftMedia, type PendingBook } from '../../state/draft';
import { describePhotos } from '../project/attachments';
import { CREATE_ACCEPT, mergeCreatePicks, pageContentProblem, readCreatePicks, type CreateDoc, type CreatePicked } from './createPicks';
import { useThumbnails } from './thumbnails';
import { Button } from '../../ui';

/**
 * Bring your own content, and be told honestly what will happen to it.
 *
 * Ported from src/components/build/ContentStep.tsx, and since 2026-09-30 doing what the demo did
 * on the phone (the wedding and café sites): several photos in one pick, each shown and described,
 * and a menu or a schedule the page is written FROM.
 *
 * Every decision about a file comes from pure, tested functions (createPicks.ts, and the shared
 * pickContent/attachments modules) — the screen only shows them.
 *
 * - A PHOTO is re-encoded as a JPEG under a name the page can use, shown as a thumbnail, and given
 *   a one-line description in a box the person can change or clear (owner decision D2). The build
 *   is told what the box says: that is what picks the hero and writes the alt text and captions.
 * - A SHORT TEXT (up to 20,000 characters, 3 files, 40,000 in all) is used on the page by default
 *   (owner decision D1) — a menu's prices, a weekend's times. Deep-dive F8: the step used to make
 *   every file a "book" that the model never sees, and demanded an attribution for a menu.
 * - A LONG TEXT is stored beside the app and read a part at a time, never rewritten by the model,
 *   and it is only handed to the build once it says where it came from: the server refuses a text
 *   with no attribution, and inventing one would defeat the reason it is required.
 */
export default function ContentStep() {
  const attachedMedia = useDraft((s) => s.attachedMedia);
  const attachedDocs = useDraft((s) => s.attachedDocs);
  const book = useDraft((s) => s.pendingBook);
  const setAttachedDoc = useDraft((s) => s.setAttachedDoc);
  const setPendingBook = useDraft((s) => s.setPendingBook);
  const setAttachedDocs = useDraft((s) => s.setAttachedDocs);
  const setAttachedMedia = useDraft((s) => s.setAttachedMedia);

  const [problems, setProblems] = useState<string[]>([]);
  /** Picks still being read. A count, so the first to finish does not say the second is done. */
  const [reading, setReading] = useState(0);
  const busy = reading > 0;
  /** Photos whose description is still being asked for, by name. */
  const [describing, setDescribing] = useState<Set<string>>(() => new Set());
  const input = useRef<HTMLInputElement>(null);

  /*
   * Back from Stripe only the FINISHED document comes back (the stash keeps attachedDoc, not the
   * book in progress), so the card is rebuilt from it — under its own file name, which the card
   * used to lose and show as "Your document".
   */
  useEffect(() => {
    const { pendingBook, attachedDoc: doc } = useDraft.getState();
    if (!pendingBook && doc) {
      setPendingBook({
        fileName: doc.fileName ?? doc.title,
        text: doc.text,
        title: doc.title,
        attribution: doc.attribution,
        headingPattern: doc.headingPattern,
      });
    }
  }, [setPendingBook]);

  // Only a COMPLETE long text reaches the build. An incomplete one is not half-attached; it is
  // not attached, which is why this clears the store rather than leaving a stale value. (With no
  // book at all, the handlers that took it away cleared it; on arrival the effect above may still
  // be rebuilding the book from a finished document, which must not be cleared from under it.)
  const ready = documentReady(book);
  useEffect(() => {
    if (!book) return;
    setAttachedDoc(ready
      ? {
        title: book.title.trim(),
        attribution: book.attribution.trim(),
        text: book.text,
        headingPattern: book.headingPattern.trim(),
        fileName: book.fileName,
      }
      : null);
  }, [book, ready, setAttachedDoc]);

  /** The long text goes, and with it whatever of it was attached. */
  function dropBook(): void {
    setPendingBook(null);
    setAttachedDoc(null);
  }

  // What the split will actually produce — the server's own splitter, so the number shown here
  // is the number of parts that will exist.
  const partCount = useMemo(
    () => (book ? previewParts(book.text, book.headingPattern.trim() || undefined).length : 0),
    [book],
  );

  const photos = useMemo(() => attachedMedia.filter((m) => m.contentType === 'image/jpeg'), [attachedMedia]);
  const clips = useMemo(() => attachedMedia.filter((m) => m.contentType !== 'image/jpeg'), [attachedMedia]);
  const thumbs = useThumbnails(photos);

  const docs: CreateDoc[] = [
    ...attachedDocs.map((d) => ({ ...d, use: 'page' as const })),
    ...(book ? [{ name: book.fileName, text: book.text, use: 'reader' as const }] : []),
  ];

  /** The draft's files and texts, as the pick functions see them. */
  function picked(d: ReturnType<typeof useDraft.getState>): CreatePicked {
    return {
      media: d.attachedMedia,
      docs: [
        ...d.attachedDocs.map((doc) => ({ ...doc, use: 'page' as const })),
        ...(d.pendingBook ? [{ name: d.pendingBook.fileName, text: d.pendingBook.text, use: 'reader' as const }] : []),
      ],
    };
  }

  async function attach(files: File[]): Promise<void> {
    setReading((n) => n + 1);
    setProblems([]);
    try {
      const read = await readCreatePicks(files, picked(useDraft.getState()));
      /*
       * Merged into the draft as it is NOW, after the await — never a copy taken before it.
       * Review finding (2026-09-30): the step wrote back the lists it had copied before reading,
       * so a photo's line that arrived while a second pick was re-encoded was wiped (and never
       * asked for again), a caption typed in that window went with it, and a photo or document
       * removed in it came back. Nothing below awaits, so nothing can change under the merge.
       */
      const now = useDraft.getState();
      const merged = mergeCreatePicks(picked(now), read);
      setProblems([...read.problems, ...merged.problems]);
      if (merged.added.length) setAttachedMedia(merged.media);
      // A pick with no text in it leaves the texts, and the long one's title and source, alone.
      if (read.docs.length) {
        setAttachedDocs(merged.docs.filter((d) => d.use === 'page').map(({ name, text }) => ({ name, text })));
        const reader = merged.docs.find((d) => d.use === 'reader');
        const was = now.pendingBook;
        if (!reader) {
          dropBook();
        } else if (!was || was.fileName !== reader.name || was.text !== reader.text) {
          // A NEW long text takes the new file's title. Attribution is left alone — a second file
          // from the same source usually has the same one.
          setPendingBook({
            fileName: reader.name,
            text: reader.text,
            title: titleFromFileName(reader.name),
            attribution: was?.attribution ?? '',
            headingPattern: '',
          });
        }
      }
      const added = merged.added.filter((m) => m.contentType === 'image/jpeg');
      if (added.length) void describe(added);
    } finally {
      setReading((n) => n - 1);
      // So the same file can be picked again after being removed.
      if (input.current) input.current.value = '';
    }
  }

  /**
   * Ask for each new photo's line, and put it in the box — ONLY where nothing has been said yet —
   * and keep the describer's pick for the top of the page with the photo.
   * The person may have typed while it was out, or cleared the box; either wins. A failure is an
   * empty box: the call never throws and nothing waits for it.
   */
  async function describe(added: DraftMedia[]): Promise<void> {
    const names = added.map((m) => m.name);
    setDescribing((s) => new Set([...s, ...names]));
    try {
      const lines = await describePhotos(added.map((m) => ({ name: m.name, file: m.file })));
      const now = useDraft.getState().attachedMedia;
      if (!names.some((n) => lines[n])) return;
      setAttachedMedia(
        now.map((m) => {
          const line = lines[m.name];
          // The same photo, not a later one that took its name after a remove.
          if (!line || !added.some((a) => a.file === m.file)) return m;
          return {
            ...m,
            ...(m.description === undefined ? { description: line.description } : {}),
            // Whoever wrote the line, the describer's pick for the top is about the photo.
            ...(line.hero ? { hero: true as const } : {}),
          };
        }),
      );
    } finally {
      setDescribing((s) => new Set([...s].filter((n) => !names.includes(n))));
    }
  }

  function setDescription(name: string, description: string): void {
    setAttachedMedia(useDraft.getState().attachedMedia.map((m) => (m.name === name ? { ...m, description } : m)));
  }

  function removeMedia(name: string): void {
    setAttachedMedia(useDraft.getState().attachedMedia.filter((m) => m.name !== name));
  }

  function removeDoc(doc: CreateDoc): void {
    setProblems([]);
    if (doc.use === 'reader') dropBook();
    else setAttachedDocs(useDraft.getState().attachedDocs.filter((d) => d.name !== doc.name));
  }

  function useFor(doc: CreateDoc, use: 'page' | 'reader'): void {
    if (doc.use === use) return;
    if (use === 'reader') {
      setAttachedDocs(useDraft.getState().attachedDocs.filter((d) => d.name !== doc.name));
      setPendingBook({ fileName: doc.name, text: doc.text, title: titleFromFileName(doc.name), attribution: '', headingPattern: '' });
    } else {
      setAttachedDocs([...useDraft.getState().attachedDocs, { name: doc.name, text: doc.text }]);
      dropBook();
    }
  }

  function editBook(change: Partial<PendingBook>): void {
    const now = useDraft.getState().pendingBook;
    if (now) setPendingBook({ ...now, ...change });
  }

  return (
    <div className="content-step">
      <p className="type-body plan-detail">
        Optional. Add your photos and they go on the site — a short text like a menu or a schedule
        is written into the page, and a long one is kept for people to read.
      </p>

      <input
        ref={input}
        id="attach-file"
        type="file"
        multiple
        accept={CREATE_ACCEPT}
        className="visually-hidden"
        aria-label="Attach photos, documents, sound or video"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length) void attach(files);
        }}
      />
      <Button
        title={busy ? 'Reading…' : attachedMedia.length || docs.length ? 'Add more' : 'Add photos or files'}
        variant="secondary"
        loading={busy}
        onPress={() => input.current?.click()}
        accessibilityLabel="Add photos, documents, sound or video"
      />

      {problems.length > 0 && (
        <div role="alert" className="type-caption field-error-text">
          {problems.map((p) => <p key={p} className="content-problem">{p}</p>)}
        </div>
      )}

      {photos.length > 0 && (
        <div className="card clay-card content-card">
          <h3 className="type-heading">
            {photos.length} photo{photos.length === 1 ? '' : 's'}
          </h3>
          <p className="type-caption plan-detail">
            Say what each one shows — it decides which goes at the top, and it is what people who
            can’t see the photo hear.
          </p>
          <ul className="content-photos">
            {photos.map((m) => (
              <li key={m.name} className="content-photo">
                {thumbs[m.name] ? (
                  <img className="content-thumb" src={thumbs[m.name]} alt="" />
                ) : (
                  <span className="content-thumb" aria-hidden="true" />
                )}
                <div className="content-photo-body">
                  <div className="content-media-row">
                    <span className="type-caption">{m.name}</span>
                    <button
                      type="button"
                      className="chip-button"
                      aria-label={`Remove ${m.name}`}
                      onClick={() => removeMedia(m.name)}
                    >
                      Remove
                    </button>
                  </div>
                  <textarea
                    className="field content-caption"
                    rows={2}
                    maxLength={140}
                    aria-label={`Describe ${m.name}`}
                    placeholder={describing.has(m.name) && m.description === undefined ? 'Describing…' : 'What’s in this photo?'}
                    value={m.description ?? ''}
                    onChange={(e) => setDescription(m.name, e.target.value)}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {clips.length > 0 && (
        <div className="card clay-card content-card">
          <h3 className="type-heading">
            {clips.length} sound{clips.length === 1 ? '' : 's'} and video{clips.length === 1 ? '' : 's'}
          </h3>
          <ul className="plan-review-list">
            {/* The NAME is what the app will be written against, so it is the thing to show. */}
            {clips.map((m) => (
              <li key={m.name} className="content-media-row">
                <span className="type-body">{m.name}</span>
                <button type="button" className="chip-button" aria-label={`Remove ${m.name}`} onClick={() => removeMedia(m.name)}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {docs.map((doc) => {
        const reason = doc.use === 'page'
          ? null
          : pageContentProblem(doc, attachedDocs.filter((d) => d.name !== doc.name));
        const otherReader = doc.use === 'page' && book ? book.fileName : null;
        return (
          <div key={`${doc.use}:${doc.name}`} role="group" aria-label={doc.name} className="card clay-card content-card">
            <div className="content-card-head">
              <h3 className="type-heading">{doc.name}</h3>
              <button type="button" className="chip-button" aria-label={`Remove ${doc.name}`} onClick={() => removeDoc(doc)}>
                Remove
              </button>
            </div>
            <p className="type-caption plan-detail">{doc.text.length.toLocaleString('en-US')} characters</p>

            <div role="radiogroup" aria-label={`How to use ${doc.name}`} className="content-uses">
              <label className="plan-check">
                <input
                  type="radio"
                  name={`use-${doc.name}`}
                  checked={doc.use === 'page'}
                  disabled={!!reason}
                  onChange={() => useFor(doc, 'page')}
                />
                <span>
                  <span className="type-body">Use it on the page</span>
                  <span className="type-caption plan-detail">
                    {reason ?? 'Its words — prices, times, names — are written into the site.'}
                  </span>
                </span>
              </label>
              <label className="plan-check">
                <input
                  type="radio"
                  name={`use-${doc.name}`}
                  checked={doc.use === 'reader'}
                  disabled={!!otherReader}
                  onChange={() => useFor(doc, 'reader')}
                />
                <span>
                  <span className="type-body">A long text people read</span>
                  <span className="type-caption plan-detail">
                    {otherReader
                      ? `One long text can be read in an app — remove ${otherReader} first.`
                      : 'Kept as it is and read a part at a time, never rewritten.'}
                  </span>
                </span>
              </label>
            </div>

            {doc.use === 'reader' && book && (
              <>
                <label className="type-caption field-label" htmlFor="doc-title">Title</label>
                <input
                  id="doc-title"
                  className="field"
                  value={book.title}
                  placeholder="The title readers see"
                  onChange={(e) => editBook({ title: e.target.value })}
                />

                <label className="type-caption field-label" htmlFor="doc-attribution">Where it came from</label>
                <input
                  id="doc-attribution"
                  className="field"
                  value={book.attribution}
                  placeholder="Who wrote it, or which edition"
                  onChange={(e) => editBook({ attribution: e.target.value })}
                />
                {!book.attribution.trim() && (
                  <p className="type-caption field-error-text">
                    Needed — it is shown to readers beside the text.
                  </p>
                )}

                <label className="type-caption field-label" htmlFor="doc-pattern">Split on (optional)</label>
                <input
                  id="doc-pattern"
                  className="field"
                  value={book.headingPattern}
                  placeholder="Chapter \d+"
                  onChange={(e) => editBook({ headingPattern: e.target.value })}
                />

                {/* The real number, from the real splitter — not an estimate. */}
                <p className="type-caption plan-detail">
                  {partCount === 1
                    ? 'Splits into 1 part. Add a pattern above to break it up.'
                    : `Splits into ${partCount} parts.`}
                </p>
                {!ready && (
                  <p className="type-caption plan-detail">
                    Not attached yet — fill in the title and where it came from.
                  </p>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
